import { useCallback, useContext, useEffect, useState } from "react";
import { SearchCircleIcon } from "@heroicons/react/solid";
import useMountStatus from "../../../../hooks/useMountStatus";

import { NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { DashboardContext } from "../../../../context/DashboardContext";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import AuditLogRow from "./AuditLogRow";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import PaginatedSearch from "../../../SharedComponents/PaginatedSearch";

import {
    AUDIT_LOG_CATEGORIES,
    AUDIT_LOG_COLUMN_NAMES,
    AUDIT_LOG_SEVERITY,
    SAMPLE_AUDIT_LOGS,
    SEVERITY_STYLES,
} from "./AuditLogData";

function FilterPill({ label, active, colour, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={
                "text-xs font-medium p-1 px-3 rounded-full transition-opacity " +
                (active ? colour + " opacity-100" : "text-slate-500 bg-slate-100 opacity-70 hover:opacity-100")
            }
        >
            {label}
        </button>
    );
}

function AuditLogs() {
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);

    // Active category/severity filter – null means "All"
    const [activeCategory, setActiveCategory] = useState(null);
    const [activeSeverity, setActiveSeverity] = useState(null);

    const unmounted = useMountStatus();

    // eslint-disable-next-line no-unused-vars
    const context = useContext(DashboardContext);

    // Replace the SAMPLE_AUDIT_LOGS reference below with a real API call when
    // the backend endpoint is available, e.g.:
    //   const response = await getAuditLogs();
    //   setLogs(response);
    const fetchLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            // Simulate async fetch – swap for real API call here
            await new Promise((resolve) => setTimeout(resolve, 400));
            if (unmounted) return;
            setLogs(SAMPLE_AUDIT_LOGS);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLogsLoading(false);
    }, [unmounted]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        let result = logs;
        if (activeCategory) result = result.filter((l) => l.category === activeCategory);
        if (activeSeverity) result = result.filter((l) => l.severity === activeSeverity);
        setFilteredLogs(result);
    }, [logs, activeCategory, activeSeverity]);

    const categoryFilters = (
        <>
            <FilterPill
                label="All categories"
                active={!activeCategory}
                colour="text-primary-600 bg-primary-100"
                onClick={() => setActiveCategory(null)}
            />
            {Object.values(AUDIT_LOG_CATEGORIES).map((cat) => (
                <FilterPill
                    key={cat}
                    label={cat}
                    active={activeCategory === cat}
                    colour="text-primary-600 bg-primary-100"
                    onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                />
            ))}
        </>
    );

    const severityFilters = (
        <>
            <FilterPill
                label="All severities"
                active={!activeSeverity}
                colour="text-primary-600 bg-primary-100"
                onClick={() => setActiveSeverity(null)}
            />
            {Object.values(AUDIT_LOG_SEVERITY).map((sev) => (
                <FilterPill
                    key={sev}
                    label={sev}
                    active={activeSeverity === sev}
                    colour={SEVERITY_STYLES[sev]}
                    onClick={() => setActiveSeverity(activeSeverity === sev ? null : sev)}
                />
            ))}
        </>
    );

    return (
        <AccessControl
            requiredPermission={PERMISSIONS.ACCESS_PSYCHPRESS_PORTAL}
            noAccessType={NO_ACCESS_TYPES.REDIRECT}
        >
            <DashboardContainer title={"Audit Log"} titleIcon={<SearchCircleIcon className="block h-5 w-5 mr-2" />}>
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <PaginatedSearch
                    loading={logsLoading}
                    items={filteredLogs}
                    itemName={"Audit Logs"}
                    searchAttributes={["event", "user", "category", "severity", "ipAddress", "description"]}
                    columnNames={AUDIT_LOG_COLUMN_NAMES}
                    ItemRow={AuditLogRow}
                    customPlaceholder="Search logs by event, user, category…"
                    filters={
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap gap-2">{categoryFilters}</div>
                            <div className="flex flex-wrap gap-2">{severityFilters}</div>
                        </div>
                    }
                />
            </DashboardContainer>
        </AccessControl>
    );
}

export default AuditLogs;
